import React, { FC, useEffect, useState } from "react";
import moment, { Moment } from "moment";

import CtaComponent from "../../../components/reusable-ui/cta";

import { CTA_SIZE, CTA_TYPE, DATE_TIME_FORMATS, optionsForDates } from "../../../data/constants";
import { enumerateDaysBetweenDates } from "../../../helpers/moment-helper";
import { cloneDeep, set } from "lodash";
import "./style.scss";
import BranchFilterComponent from "../../../components/reusable-ui/branch-filter";
import DateRangePicker from "../../../components/reusable-ui/date-range-picker";
import { RootState } from "../../../redux/store";
import { useSelector } from "react-redux";
import { exportReportData } from "../../../redux/slices";
import { useDispatch } from "react-redux";
import { isMobileDevice } from "../../../helpers/device-info-helper";
import { Menu, MenuItem } from "@mui/material";
import DatePickerWrapper from "../../../components/reusable-ui/date-picker-wrapper";
import { Tooltip as MuiTooltip } from '@mui/material';

interface Props {
  viewPnlData: any;
  openExpenseModal: any;
  setApiStateModalData: any;
  setActionMode: any;
  setSelectedDateRange?: (data : any) => void
}

const TopActionBar: FC<Props> = (props: Props) => {
  const { viewPnlData, openExpenseModal, setApiStateModalData, setActionMode, setSelectedDateRange } =
    props;
  const [filterConfig, setFilterConfig] = useState<any>({
    branches: [],
    periods: [],
  });
  const [firstTime, setFirstTime] = useState(true)
   
  useEffect(() => {
      if(firstTime && filterConfig?.branches?.length && filterConfig?.periods?.length)
      {
        viewPnlData(filterConfig);
        setFirstTime(false)
      }
  }, [filterConfig])

  const { permittedBranchesData } = useSelector((state: RootState) => ({
    permittedBranchesData: state.businessReducer.userData.permittedBranches,
  }));
  const dispatch = useDispatch();
  const { userData } = useSelector((state: RootState) => ({
    userData: state.businessReducer.userData,
  }));
  const ACTIONS_LIST = [
    { id: "upload_expense", text: "Upload Expense" },
    { id: "download_expense", text: "Download Expense" },
  ];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLElement>,
    index: number
  ) => {
    openExpenseModal(true);
    setActionMode(index === 1);
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFilterChange = (value: any, fieldToUpdate: string) => {
    let dataToUpdate = cloneDeep(filterConfig);
    set(dataToUpdate, fieldToUpdate, value);
    setFilterConfig(dataToUpdate);
  };

  const onDateRangeSubmission = (data: {
    startDate: Moment;
    endDate: Moment;
  }) => {
    setSelectedDateRange?.(data)
    let periods: any = [];
    if (data.startDate && data.endDate) {
      periods = enumerateDaysBetweenDates(
        data.startDate,
        data.endDate,
        DATE_TIME_FORMATS.yearMonthDay
      );
    }
    handleFilterChange(periods, "periods");
  };

  /**
   * The dates more than 3 years will always be disabled
   * @param current
   */
  const disabledDate = (current: any) => {
    return (
      current &&
      (current >= moment().endOf("day") || moment().diff(current, "days") > 89)
    );
  };

  const handleDownloadStatement = () => {
    const callback = (res: any) => {
      if (!res?.code) {
        setApiStateModalData({
          show: true,
          modalText:
            "Report is getting generated. You can check the status and download it in Download Section.",
        });
      }
    };
    dispatch(
      exportReportData({
        dataToPost: {
          branchIds: filterConfig?.branches,
          periods: filterConfig?.periods,
          source: "profitandloss",
          saveReport: true,
          name: `${userData?.name?.firstName} ${
            userData?.name?.lastName || ""
          }`,
          groupByKeys: [],
        },
        callback,
      })
    );
  };

  function getTooltipContent() {
    return (
      <div className="bg-black p-[10px] rounded-[6px]">
        <span>{'Download Statement'}</span>
      </div>
    )
  }

  function renderActionCta(isMobile: boolean = false): React.JSX.Element {
    return (
      <>
        {isMobile ? (
          <button
            id="basic-button"
            aria-controls={open ? "action-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={handleClick}
            className={``}
          >
            <div className={`rista-sprite ic-kebab-menu-black`}></div>
          </button>
        ) : (
          <button
            id="basic-button"
            aria-controls={open ? "action-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={handleClick}
            className={`h-40 absolute right-[20px] z-[11] bg-white flex items-center rounded-[8px] border-[1px] border-[#D6D6D6] ${
              open
                ? "border-black text-[#000]"
                : "border-[#D6D6D6] text-[#343434]/70"
            } hover:border-black hover:text-[#000] text-16 leading-none
        px-16 py-8`}
          >
            Expense Data
            <div className={`rista-sprite ic-dropdown ml-8 mb-2`}></div>
          </button>
        )}

        <Menu
          id="action-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          {ACTIONS_LIST.map(
            (item: { id: string; text: string }, index: number) => (
              <MenuItem
                key={index}
                onClick={(event) => handleMenuItemClick(event, index)}
              >
                {item.text}
              </MenuItem>
            )
          )}
        </Menu>
      </>
    );
  }

  function renderActionCtaDesktop(): React.JSX.Element {
    let enableApplyCta =
      filterConfig?.branches?.length && filterConfig?.periods?.length;
    return (
      <>
        <CtaComponent
          ctaText="View"
          ctaSize={CTA_SIZE.small}
          ctaType={CTA_TYPE.active_cta}
          onCtaClick={() => viewPnlData(filterConfig)}
          customClass={"mr-[20px]"}
          isDisabled={!enableApplyCta}
        />
        <MuiTooltip
          arrow={true}
          enterTouchDelay={0}
          title={getTooltipContent()}
          componentsProps={{ tooltip: { sx: { background: "transparent", } } }}
        >
          <div
            className={`rista-sprite ic-download-cta-green cursor-pointer mr-[20px] grow-0 ${enableApplyCta ? "" : "opacity-50 pointer-events-none"
              }`}
            onClick={handleDownloadStatement}
          ></div>
        </MuiTooltip>
        {renderActionCta()}
      </>
    );
  }

  function renderActionCtaMobile(): React.JSX.Element {
    let enableApplyCta =
      filterConfig?.branches?.length && filterConfig?.periods?.length;
    return (
      <>
        <div className="mt-16 flex items-center w-full">
          <CtaComponent
            ctaText="View P&L Data"
            ctaSize={CTA_SIZE.small}
            ctaType={CTA_TYPE.active_cta}
            onCtaClick={() => viewPnlData(filterConfig)}
            customClass={"grow"}
            isDisabled={!enableApplyCta}
          />
          <div
            className={`rista-sprite ic-download-cta-green cursor-pointer mx-20 grow-0 ${
              enableApplyCta ? "" : "opacity-50 pointer-events-none"
            }`}
            onClick={handleDownloadStatement}
          ></div>
          {renderActionCta(true)}
        </div>
      </>
    );
  }

  return (
    <div
      className={`top-action-bar flex items-center flex-wrap gap-y-20 sm:gap-y-7 p-20 bg-white relative
        sm:flex-col sm:p-16 sm:items-start
      `}
    >
      {/* <DateRangePicker
        dateFormat={DATE_TIME_FORMATS.dayMonthYear}
        disabledDate={disabledDate}
        timeDifference={90}
        errorMessage={`You can select date range max upto 90 days!`}
        onSubmit={onDateRangeSubmission}
        defaultDateRange={[null, null]}
        customClass="!border-[#bdbdbd]"
      ></DateRangePicker> */}
        <DatePickerWrapper
          options={optionsForDates}
          drpdownUniqueKey={'value'}
          placeholder="please select your date range"
          preSelectedDropdownValue={optionsForDates[0].value}
          getDateRange={(data) => onDateRangeSubmission(data)}
        />
      <BranchFilterComponent
        branchDataList={permittedBranchesData}
        onApplyCtaClick={(branches: any) =>
          handleFilterChange(branches, "branches")
        }
        selectAllBranches={true}
        parentClass={`ml-20 mr-20 !h-[40px] w-[200px] sm:w-full sm:ml-0 sm:mt-16`}
        customClass={"!h-[40px] sm:text-[14px] !border-[#bdbdbd]"}
      ></BranchFilterComponent>
      {isMobileDevice() ? renderActionCtaMobile() : renderActionCtaDesktop()}
    </div>
  );
};

export default TopActionBar;
